import type * as React from "react";
import { useTranslation } from "react-i18next";
import type { DraftKind } from "@steelbill/types";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";

/**
 * The other device won a concurrent edit of the same saved form (§7.2).
 *
 * NOT A TOAST. Autosave has STOPPED — every further keystroke on this screen is
 * being kept nowhere — so this has to stay on screen until it is acted on. A
 * message that fades after four seconds would leave him typing into a form that
 * is no longer saving, which is the exact failure the version column exists to
 * prevent.
 *
 * RELOAD RATHER THAN MERGE. There is no honest way to combine two sets of typed
 * lines, and guessing would silently drop one of them. The newer version is
 * shown; he reads it and continues from there.
 *
 * ONE COMPONENT FOR EVERY EDITOR — the only difference is the noun, and three
 * copies of this alert is how the bill's version keeps the reload button and the
 * others quietly lose it.
 *
 * KEYED BY KIND, NOT A TERNARY — the third time this exact bug has been fixed.
 * It read `kind === "INVOICE" ? …Invoice : …Quotation`, which is right for two
 * kinds and silently wrong for the third: `ChallanEditorScreen` renders this with
 * `kind="CHALLAN"`, so a challan edited on two devices told the owner "This
 * QUOTATION was changed on another device" — about a document that is not a
 * quotation, on the one screen whose job is explaining why his typing stopped
 * being saved. A `Record<DraftKind, …>` cannot fall through; a fourth kind fails
 * the build here instead. See features/drafts/draft-kind.tsx.
 */

const CONFLICT_TITLE_KEY: Record<DraftKind, string> = {
  INVOICE: "draft.conflictTitleInvoice",
  QUOTATION: "draft.conflictTitleQuotation",
  CHALLAN: "draft.conflictTitleChallan",
};

const CONFLICT_HELP_KEY: Record<DraftKind, string> = {
  INVOICE: "draft.conflictHelpInvoice",
  QUOTATION: "draft.conflictHelpQuotation",
  CHALLAN: "draft.conflictHelpChallan",
};
export function DraftConflictAlert({
  kind,
}: {
  kind: DraftKind;
}): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <Alert>
      <AlertTitle>{t(CONFLICT_TITLE_KEY[kind])}</AlertTitle>
      <AlertDescription className="flex flex-col items-start gap-2">
        <span>{t(CONFLICT_HELP_KEY[kind])}</span>
        <Button
          variant="outline"
          onClick={() => {
            window.location.reload();
          }}
        >
          {t("draft.reload")}
        </Button>
      </AlertDescription>
    </Alert>
  );
}
